import { Injectable } from '@angular/core';
import { Cache, cacheable } from '../cache/index';
import { OiLink } from '../core-data-model/OiLink.model';
import { SearchState } from '../core-data-model/SearchState.model';
import {
    UrlShortenerResult,
    ShortenedUrlInfoServer,
    ShortenedOi1UrlFullInfoServer
} from '../core-data-model/UrlShortenerApi.serverinterface';
import { UrlShortenerTransformers } from '../core-services/UrlShortenerApi.datatransform';
import { UrlShortenerQueryGeneratorService } from '../core-services/UrlShortenerQueryGenerator.service';
import { ApiService } from '../system-services/Api.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';

@Injectable({
    providedIn: 'root'
})
export class UrlShortenerApi {
    private shortedUrlCache = new Cache<UrlShortenerResult, string>(
        'shortenedUrlCache',
        (longUrl: string) => longUrl
    );

    constructor(
        private api: ApiService,
        private transform: UrlShortenerTransformers,
        private urlShortenerQueryGeneratorService: UrlShortenerQueryGeneratorService,
        private userProfileService: UserProfileSettingsService,
        private userProfile: UserProfileSettingsService
    ) {}

    @cacheable(
        function (this: UrlShortenerApi) {
            return this.shortedUrlCache;
        },
        (longUrl: string) => longUrl
    )
    createShortenedUrl(
        longUrl: string,
        linkName: string,
        companyId = this.userProfile.currentCompany
    ): Promise<UrlShortenerResult> {
        return this.api
            .post(`/api/v1/companies/${companyId}/UrlShortener`, {
                longUrl,
                linkName
            })
            .then(([response]) => response);
    }
    updateShortenedUrl(
        shortenedUrl: string,
        longUrl: string,
        linkName = '',
        companyId = this.userProfile.currentCompany
    ): Promise<any> {
        return this.api
            .patch(
                `/api/v1/companies/${companyId}/urlShortener?url=${shortenedUrl}`,
                {
                    longUrl,
                    linkName
                }
            )
            .then(([response]) => response);
    }
    deleteShortenedUrl(
        shortenedUrl: string,
        companyId = this.userProfile.currentCompany
    ): Promise<any> {
        return this.api.delete(
            `/api/v1/companies/${companyId}/urlShortener?url=${shortenedUrl}`
        );
    }

    getShortenedUrl(
        shortenedUrl: string,
        companyId = this.userProfile.currentCompany
    ): Promise<OiLink> {
        return this.api
            .get<ShortenedUrlInfoServer>(
                `/api/v1/companies/${companyId}/urlShortener?url=${shortenedUrl}`
            )
            .then(([response]) => this.transform.serverToClient(response));
    }

    getAllShortenedUrls(
        pristineSearchState: SearchState,
        getCount = false,
        companyId = this.userProfile.currentCompany
    ): Promise<OiLink[]> {
        if (getCount) {
            let totalShortenedUrlsCount = 0;
            return this.getAllShortenedUrlsCount()
                .then((count) => {
                    totalShortenedUrlsCount = count;
                })
                .then(() =>
                    this.api.multiplexSearchRequest(
                        pristineSearchState,
                        (searchState) => {
                            const oDataQuery =
                                this.urlShortenerQueryGeneratorService.createODataString(
                                    searchState
                                );
                            return this.api
                                .get<
                                    ShortenedUrlInfoServer[]
                                >(`/api/v1/companies/${companyId}/urlShortener/urls?${oDataQuery}`)
                                .then(([response]) => {
                                    pristineSearchState.pageState.maxResults =
                                        totalShortenedUrlsCount;

                                    return this.transform.serverListToClientList(
                                        response
                                    );
                                });
                        }
                    )
                );
        } else {
            const oDataQuery =
                this.urlShortenerQueryGeneratorService.createODataString(
                    pristineSearchState
                );
            return this.api
                .get<
                    ShortenedUrlInfoServer[]
                >(`/api/v1/urlShortener/urls?${oDataQuery}`)
                .then(([response]) =>
                    this.transform.serverListToClientList(response)
                );
        }
    }

    getAllShortenedUrlsCount(
        companyId: string = this.userProfileService.currentCompany
    ): Promise<number> {
        return this.api
            .get<number>(`/api/v1/companies/${companyId}/urlShortener/count`)
            .then(([result]) => result);
    }

    getAllOi1Urls(
        pristineSearchState: SearchState,
        companyId = this.userProfile.currentCompany
    ): Promise<OiLink[]> {
        const oDataQuery =
            this.urlShortenerQueryGeneratorService.createODataString(
                pristineSearchState
            );
        return this.api
            .get<ShortenedOi1UrlFullInfoServer>(
                `/api/v1/companies/${companyId}/urlShortener/oi1urls?${oDataQuery}`
            )
            .then(([response]) => {
                pristineSearchState.pageState.maxResults =
                    response.totalResults;

                return this.transform.serverOi1ListToClientOi1List(
                    response.Results
                );
            });
    }
    deleteOi1Url(
        shortenedUrl: string,
        companyId = this.userProfile.currentCompany
    ): Promise<any> {
        return this.api.delete(
            `/api/v1/companies/${companyId}/urlShortener/oi1urls?url=${shortenedUrl}`
        );
    }
}
